import { useEffect, useRef, useState } from 'react';
import { api } from '../api/cliente.js';
import { hoyEn, sumarDias } from './formato.js';

/**
 * Averigua qué días de los próximos N tienen turnos libres.
 *
 * El backend responde por día, así que hay que preguntarle día por día. Se
 * hace de a tandas para no dispararle treinta pedidos juntos, y el resultado
 * se va pintando a medida que llega: el calendario aparece enseguida y los
 * días se van habilitando solos.
 *
 * Un endpoint que devuelva el mes entero de una sola vez sería lo correcto;
 * mientras no exista, esto es lo que hay.
 */
export function useDiasConTurnos({ slug, profesional, servicio, timezone, cantidad = 30 }) {
  const [dias, setDias] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [intento, setIntento] = useState(0);
  const cancelado = useRef(false);

  useEffect(() => {
    if (!profesional || !servicio) return undefined;

    cancelado.current = false;
    setCargando(true);
    setError(null);

    const primerDia = hoyEn(timezone);
    // `libre: null` = todavía no sabemos. El día se muestra deshabilitado
    // hasta que llega la respuesta, pero el calendario ya está en pantalla.
    const inicial = Array.from({ length: cantidad }, (_, i) => ({
      fecha: sumarDias(primerDia, i),
      libre: null
    }));
    setDias(inicial);

    const controlador = new AbortController();
    const TANDA = 6;

    (async () => {
      try {
        for (let desde = 0; desde < inicial.length; desde += TANDA) {
          const tanda = inicial.slice(desde, desde + TANDA);

          const respuestas = await Promise.all(tanda.map(async (dia) => {
            const datos = await api.disponibilidad(
              slug,
              { profesional: profesional.id, servicio: servicio.id, fecha: dia.fecha },
              { signal: controlador.signal }
            );
            return { fecha: dia.fecha, libre: datos.slots.length > 0 };
          }));

          if (cancelado.current) return;

          setDias((previos) => previos.map((dia) => {
            const encontrado = respuestas.find((r) => r.fecha === dia.fecha);
            return encontrado ? { ...dia, libre: encontrado.libre } : dia;
          }));
        }
      } catch (fallo) {
        if (!cancelado.current && fallo.name !== 'AbortError') {
          setError(fallo.message);
        }
      } finally {
        if (!cancelado.current) setCargando(false);
      }
    })();

    return () => {
      cancelado.current = true;
      controlador.abort();
    };
  }, [slug, profesional?.id, servicio?.id, timezone, cantidad, intento]);

  return { dias, cargando, error, reintentar: () => setIntento((n) => n + 1) };
}

/** Trae los slots de un día concreto. */
export function useSlots({ slug, profesional, servicio, fecha }) {
  const [slots, setSlots] = useState([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState(null);
  const [intento, setIntento] = useState(0);

  useEffect(() => {
    if (!profesional || !servicio || !fecha) return undefined;

    let vigente = true;
    const controlador = new AbortController();

    setCargando(true);
    setError(null);

    api.disponibilidad(
      slug,
      { profesional: profesional.id, servicio: servicio.id, fecha },
      { signal: controlador.signal }
    )
      .then((datos) => { if (vigente) setSlots(datos.slots); })
      .catch((fallo) => { if (vigente && fallo.name !== 'AbortError') setError(fallo.message); })
      .finally(() => { if (vigente) setCargando(false); });

    return () => { vigente = false; controlador.abort(); };
  }, [slug, profesional?.id, servicio?.id, fecha, intento]);

  return { slots, cargando, error, recargar: () => setIntento((n) => n + 1) };
}
